// external
import type { FC } from 'react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

// interstate components
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';

// libraries
import { formatNumberAndCents } from '@makemydeal/dr-common-utils';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

// utils
import { usePaymentCall } from '../utils/usePaymentCall';

// constants
import { RESIDUAL_FIELD_LABELS } from './constants';

export type ResidualFieldType = 'dollar' | 'percent';

export interface ProgramResidualFieldProps {
    fieldType: ResidualFieldType;
    disabled?: boolean;
    error?: string;
    'data-testid'?: string;
}

const FIELD_CONFIG = {
    dollar: {
        label: RESIDUAL_FIELD_LABELS.PROGRAM_RESIDUAL,
        unifiedLabel: RESIDUAL_FIELD_LABELS.RESIDUAL,
        placeholder: '0.00',
        prefix: '$',
        unitType: RESIDUAL_DOLLAR,
        decimalMaxLength: 2,
        decimalMinLength: 2,
        autoInsertCommas: true,
        formatFn: formatNumberAndCents,
        selector: offerReduxSelectors.getProgramResidual,
        fallbackKey: 'programResidual' as const
    },
    percent: {
        label: RESIDUAL_FIELD_LABELS.PROGRAM_RESIDUAL_PERCENT,
        unifiedLabel: RESIDUAL_FIELD_LABELS.RESIDUAL_PERCENT,
        placeholder: '0.00000',
        prefix: '%',
        unitType: RESIDUAL_PERCENT,
        decimalMaxLength: 5,
        decimalMinLength: 0,
        autoInsertCommas: false,
        formatFn: String,
        selector: offerReduxSelectors.getProgramResidualPercent,
        fallbackKey: 'programResidualPercent' as const
    }
} as const;

/**
 * Unified Program Residual field component that handles both dollar and percent variants.
 * Sends UPDATED_PROGRAM_RESIDUAL_OVERRIDE action which updates only program residual in UPQ mode.
 */
export const ProgramResidualField: FC<ProgramResidualFieldProps> = ({
    fieldType,
    disabled = false,
    error,
    'data-testid': testId
}) => {
    const config = FIELD_CONFIG[fieldType];
    const months = useSelector(offerReduxSelectors.getTermLength) as number;

    // Get current program residual value
    const residualOverride = useSelector(offerSelectors.getResidualInfoOverrideWithFallback);
    const programResidualFromState = useSelector(config.selector);
    const currentValue = programResidualFromState ?? residualOverride?.[config.fallbackKey] ?? 0;
    const isUnifiedResidualFieldsEnabled = useSelector(featureToggleSelectors.isUnifiedResidualFieldsEnabled);

    // Wrap action creator to pass unitType and months
    /* istanbul ignore next -- callback passed to usePaymentCall */
    const updateProgramResidual = useCallback(
        (value: number) => {
            return offerReduxActionCreators.updateProgramResidualOverride(
                {
                    value,
                    months,
                    unitType: config.unitType
                },
                true // makePaymentCall
            );
        },
        [months, config.unitType]
    );

    const {
        value: displayValue,
        handleChange: handleProgramResidualChange,
        handleBlur,
        handleKeyDown
    } = usePaymentCall(/* istanbul ignore next -- selector callback */ () => currentValue, updateProgramResidual, {
        formatValueToString: config.formatFn
    });

    const handleChange = useCallback(
        (e: InterstateOnChangeEvent<string>) => {
            handleProgramResidualChange(e);
        },
        [handleProgramResidualChange]
    );

    return (
        <NumericInput
            label={isUnifiedResidualFieldsEnabled ? config.unifiedLabel : config.label}
            name={`program-residual-${fieldType}`}
            onChange={handleChange}
            placeholder={config.placeholder}
            value={displayValue}
            hasError={!!error}
            errorMessage={error}
            onBlur={handleBlur}
            data-testid={testId || `program-residual-${fieldType}-input`}
            allowDecimal={true}
            decimalMaxLength={config.decimalMaxLength}
            decimalMinLength={config.decimalMinLength}
            disabled={disabled}
            inputPrefix={config.prefix}
            autoInsertCommas={config.autoInsertCommas}
            onKeyDown={handleKeyDown}
            textAlign="left"
            selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
            strictSelectOnFocus
            aria-invalid={!!error}
            aria-describedby={error ? `program-residual-${fieldType}-error` : undefined}
        />
    );
};

export default ProgramResidualField;
